import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../components/auth/auth.service';
import { ImpersonationService } from '../../components/auth/impersonation.service';
import { CampaignService } from '../campaign/shared/campaign.service';
import { CampaignFilesService } from '../campaign/shared/campaign-files.service';
import { DarkPoolService } from '../dark-pool/shared/dark-pool.service';
import { LoggerService } from '../shared/logger.service';

@Component({
  selector: 'main',
  template: require('./main.html'),
  styles: [require('./main.scss')]
})
export class MainComponent implements OnInit {
  static parameters = [AuthService, ImpersonationService, CampaignService, CampaignFilesService, DarkPoolService, LoggerService, HttpClient];
  constructor(authService: AuthService, impersonation: ImpersonationService, campaignService: CampaignService, campaignFilesService: CampaignFilesService, darkPoolService: DarkPoolService, loggerService: LoggerService, http: HttpClient) {
    this.authService = authService;
    this.impersonation = impersonation;
    this.campaignService = campaignService;
    this.campaignFilesService = campaignFilesService;
    this.darkPoolService = darkPoolService;
    this.loggerService = loggerService;
    this.http = http;
    this.currentOrg = { active: false };
    this.motivaSteps = [];
    this.loadingMotivaSteps = true;
    this.loadingExportData = false;
    this.isDemo = false;
    this.lookbackOptions = [
      { value: 1, label: 'Last 24 Hours' },
      { value: 7, label: 'Last 7 Days' },
      { value: 30, label: 'Last 30 Days' },
      { value: 90, label: 'Last 90 Days' },
      { value: 365, label: 'Last 1 year' }
    ];
    this.selectedLookback = this.lookbackOptions[1];
    this.currentPage = 1;
    this.pageSize = 10;
  }

  ngOnInit() {
    this.isAdmin = false;
    this.authService.isAdmin().then(is => {
      this.isAdmin = is;
    });

    this.authService.getCurrentOrg().then(org => {
      if(typeof org !== 'undefined') {
        this.currentOrg = org;
      }
    });

    this.orgSub = this.authService.currentOrgChanged.subscribe(org => {
      if(typeof org !== 'undefined') {
        this.currentOrg = org;
      }
    });

    this.isDemo = this.impersonation.isDemoMode();

    if(!this.isDemo) {
      this.loadRecentCampaigns();
    }
    else {
      this.loadDemo();
    }
  }

  ngOnDestroy() {
    if(this.orgSub) this.orgSub.unsubscribe();
  }

  loadRecentCampaigns() {
    this.loadingRecentCampaigns = true;
    this.campaignService
      .active(this.selectedLookback.value, true)
      .toPromise()
      .then(campaigns => {
        this.loadingMotivaSteps = false;
        this.loadingRecentCampaigns = false;
        this.motivaSteps = campaigns;
        this.motivaSteps.forEach(function(motivaStep) {
          //Add display status
          //Both Simple Bulk and Drip have sendWinnerToAllRemainingImmediately set to true.
          if(motivaStep.optimizationCriterion !== 'sendTime' && motivaStep.emailCount === 1 && typeof motivaStep.sendWinnerToAllRemainingImmediately !== 'undefined' && motivaStep.sendWinnerToAllRemainingImmediately) {
            motivaStep.displayOptimization = 'Simple';
          }
          else if(motivaStep.optimizationCriterion === 'sendTime') {
            //<< null is STO
            motivaStep.displayOptimization = 'Send Time';
          }
          else {
            motivaStep.displayOptimization = 'Message Testing';
          }

          //Calculate the sends and rates to show
          var sends = 0, opens = 0, clicks = 0, found = false;
            for(const emailId in motivaStep.latestResults) {
            found = true;
            sends += motivaStep.latestResults[emailId].sends;
            opens += (motivaStep.latestResults[emailId].sends * motivaStep.latestResults[emailId].openRate);
            clicks += (motivaStep.latestResults[emailId].sends * motivaStep.latestResults[emailId].clickthroughRate);
          }

          if(found) {
            motivaStep.sends = sends;
            motivaStep.openRate = (sends > 0 ? opens/sends : 0);
            motivaStep.clickthroughRate = (sends > 0 ? clicks/sends : 0);
          }
        });
      },
      error => {
        this.loggerService.logMessage('Error retrieving campaigns for home screen.', 'error', error);
        this.loadingMotivaSteps = false;
        this.loadingRecentCampaigns = false;
      })
      .catch(e => {
        this.loggerService.logMessage('Exception retrieving campaigns for home screen.', 'error', e);
        this.loadingMotivaSteps = false;
        this.loadingRecentCampaigns = false;
      });
  }

  loadDemo() {
    this.loadingMotivaSteps = false;
    this.motivaSteps = this.campaignFilesService.getDemoCampaignList();
  }

  onLookbackChange(lookback) {
    this.selectedLookback = lookback;
    this.loadRecentCampaigns();
  }

  getCampaignStatsForOrg(params) {
    var promise = new Promise(resolve => {
      // Get total open and click counts and deliveries for all campaigns
      this.http
        .get('/api/olap/allCampaignAdminViewStats', { params })
        .toPromise()
        .then(stats => {
          stats.forEach(campaignStats => {
            this.allCampaignStats[campaignStats.motiva_campaign_id] = {
              sends: campaignStats.total_sends,
              opens: campaignStats.total_opens,
              clicks: campaignStats.total_clicks,
              deliveries: campaignStats.total_sends - campaignStats.total_bouncebacks
            };
          });
          resolve();
        }, err => {
          console.log(`Error getting all campaign stats for org: ${params.orgId}`, err);
          // We want to continue the export with any data we do get, so resolve
          // instead of failing here.
          resolve();
        });
    });
    return promise;
  }

  exportToCsv() {
    if (!this.motivaSteps || this.motivaSteps.length === 0) {
      return;
    }

    this.loadingExportData = true;
    const csvRows = [];
    // Add header row
    csvRows.push([
      'Name',
      'Status',
      'Optimization AI',
      'Variations',
      'Contacts',
      'Sends',
      'Deliveries',
      'Total Unique Opens',
      'Total Unique Clicks',
      'Open Rate',
      'Click Rate',
      'Override FM',
      'End Date',
      'Last Update'
    ].join(','));

    this.allCampaignStats = {};
    var params = {
      orgId: this.currentOrg._id
    };

    this.getCampaignStatsForOrg(params).then(() => {
      console.log('allCampaignStats', this.allCampaignStats);
      console.log('motivaSteps', this.motivaSteps);
      this.motivaSteps.forEach(step => {
        if(this.allCampaignStats[step.campaignId]) {
          var sends = this.allCampaignStats[step.campaignId].sends;
          var totalUniqueOpens = this.allCampaignStats[step.campaignId].opens;
          var totalUniqueClicks = this.allCampaignStats[step.campaignId].clicks;
          var deliveries = this.allCampaignStats[step.campaignId].deliveries;
        }

        // Add data rows
        const row = [
          `"${step.name}"`,
          `"${this.campaignService.decodeStatus(step.status)}"`,
          `"${step.displayOptimization}"`,
          step.emailCount,
          step.uniqueContactCount,
          sends || 0,
          deliveries || 0,
          totalUniqueOpens || 0,
          totalUniqueClicks || 0,
          step.openRate ? (step.openRate * 100).toFixed(1) + '%' : '0%',
          step.clickthroughRate ? (step.clickthroughRate * 100).toFixed(1) + '%' : '0%',
          step.overrideSendFrequencyCheck,
          step.isMotionControlled,
          step.statusDate ? step.statusDate : step.updatedAt
        ].join(',');
        csvRows.push(row);
      });

      // Create and trigger download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `motiva-campaigns-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      this.loadingExportData = false;
    });
  }

  getTotalPages() {
    return Math.ceil(this.motivaSteps.length / this.pageSize);
  }

  getPages() {
    const totalPages = this.getTotalPages();
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }
}
